import React, { useState, useCallback } from 'react';
import { Upload, X, FileText, Loader2 } from 'lucide-react';
import { Button } from './ui/button';
import { Progress } from './ui/progress';

const CHUNK_SIZE = 1024 * 1024; // 1MB chunks

const FileUploader = ({ onFilesUploaded, maxFiles = 20 }) => {
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({});

  const handleFileSelect = (e) => {
    const selectedFiles = Array.from(e.target.files);
    
    if (files.length + selectedFiles.length > maxFiles) {
      alert(`Максимум ${maxFiles} файлов`);
      return;
    }

    const newFiles = selectedFiles.map(file => ({
      id: `${Date.now()}_${Math.random()}`,
      file,
      name: file.name,
      size: file.size,
      progress: 0,
      uploaded: false,
    }));

    setFiles(prev => [...prev, ...newFiles]);
  };

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    const droppedFiles = Array.from(e.dataTransfer.files);
    
    if (files.length + droppedFiles.length > maxFiles) {
      alert(`Максимум ${maxFiles} файлов`);
      return;
    }

    const newFiles = droppedFiles.map(file => ({
      id: `${Date.now()}_${Math.random()}`,
      file,
      name: file.name,
      size: file.size,
      progress: 0,
      uploaded: false,
    }));

    setFiles(prev => [...prev, ...newFiles]);
  }, [files.length, maxFiles]);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
  }, []);

  const removeFile = (fileId) => {
    setFiles(prev => prev.filter(f => f.id !== fileId));
  };

  const uploadFile = async (fileData) => {
    const { file, id } = fileData;
    const totalChunks = Math.ceil(file.size / CHUNK_SIZE);
    const fileId = `${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    
    let uploadedInfo = null;

    for (let chunkIndex = 0; chunkIndex < totalChunks; chunkIndex++) {
      const start = chunkIndex * CHUNK_SIZE;
      const end = Math.min(start + CHUNK_SIZE, file.size);
      const chunk = file.slice(start, end);

      const formData = new FormData();
      formData.append('file_id', fileId);
      formData.append('chunk_index', chunkIndex);
      formData.append('total_chunks', totalChunks);
      formData.append('filename', file.name);
      formData.append('chunk', chunk);

      try {
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/upload-chunk`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
          },
          body: formData,
        });

        if (!response.ok) throw new Error('Upload failed');

        const result = await response.json();
        
        const progress = Math.round(((chunkIndex + 1) / totalChunks) * 100);
        setUploadProgress(prev => ({ ...prev, [id]: progress }));

        if (result.completed) {
          uploadedInfo = {
            file_id: result.file_id,
            filename: result.filename,
            file_path: result.file_path,
            file_size: result.file_size,
          };
        }
      } catch (error) {
        console.error('Chunk upload error:', error);
        throw error;
      }
    }

    return uploadedInfo;
  };

  const handleUploadAll = async () => {
    setUploading(true);
    const uploadedFiles = [];

    try {
      for (const fileData of files) {
        if (!fileData.uploaded) {
          const uploadedInfo = await uploadFile(fileData);
          uploadedFiles.push(uploadedInfo);
          
          setFiles(prev => prev.map(f => 
            f.id === fileData.id ? { ...f, uploaded: true } : f
          ));
        }
      }

      onFilesUploaded(uploadedFiles);
      setFiles([]);
      setUploadProgress({});
    } catch (error) {
      console.error('Upload error:', error);
      alert('Ошибка загрузки файлов');
    } finally {
      setUploading(false);
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  return (
    <div className="w-full">
      <div
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        className="border-2 border-dashed border-gray-600 rounded-lg p-8 text-center hover:border-gray-500 transition-colors cursor-pointer bg-gray-900"
      >
        <input
          type="file"
          multiple
          onChange={handleFileSelect}
          className="hidden"
          id="file-upload"
          accept=".pdf,.png,.jpg,.jpeg,.doc,.docx,.txt,.dcm,.dicom,.xml,.csv,.xlsx,.xls"
        />
        <label htmlFor="file-upload" className="cursor-pointer">
          <Upload className="mx-auto mb-4 text-gray-400" size={48} />
          <p className="text-gray-300 mb-2">
            Перетащите файлы сюда или нажмите для выбора
          </p>
          <p className="text-sm text-gray-500">
            Максимум {maxFiles} файлов. PDF, изображения, документы
          </p>
        </label>
      </div>

      {files.length > 0 && (
        <div className="mt-6 space-y-3">
          {files.map((fileData) => (
            <div
              key={fileData.id}
              className="flex items-center justify-between bg-gray-900 p-4 rounded-lg border border-gray-700"
            >
              <div className="flex items-center flex-1 mr-4">
                <FileText className="text-blue-400 mr-3" size={24} />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-white truncate">
                    {fileData.name}
                  </p>
                  <p className="text-xs text-gray-400">
                    {formatFileSize(fileData.size)}
                  </p>
                  {uploadProgress[fileData.id] > 0 && (
                    <Progress 
                      value={uploadProgress[fileData.id]} 
                      className="mt-2 h-1"
                    />
                  )}
                </div>
              </div>
              {!uploading && !fileData.uploaded && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => removeFile(fileData.id)}
                  className="text-red-400 hover:text-red-300"
                >
                  <X size={18} />
                </Button>
              )}
              {fileData.uploaded && (
                <span className="text-green-400 text-sm">✓ Загружено</span>
              )}
            </div>
          ))}

          <Button
            onClick={handleUploadAll}
            disabled={uploading || files.every(f => f.uploaded)}
            className="w-full mt-4 bg-blue-600 hover:bg-blue-700"
          >
            {uploading ? (
              <>
                <Loader2 className="mr-2 animate-spin" size={18} />
                Загрузка...
              </>
            ) : (
              `Загрузить все (${files.length})`
            )}
          </Button>
        </div>
      )}
    </div>
  );
};

export default FileUploader;
